import { createClient } from '@feedfm/clips-api-client';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import LoginScreen from './DemoAppLoginScreen';

/**
 * The demo app context provides the auth token and its setter to any components that need it. Use
 * useDemoAppContext to obtain the context. The provider must be wrapped around the component tree
 * that relies on it.
 *
 * The provider will automatically interact with localStorage to save/remember auth tokens between
 * page views.
 */

export interface IDemoAppContext {
  /** The user has (or has not) provided the generic password at the front door. */
  doorPasswordProvided: boolean;
  setDoorPasswordProvided: (value: boolean) => void;

  /** The active client ID, which may be '' if there is none. */
  clientId: string;
  setClientid: (value: string) => void;

  /** The backend URL for API calls. */
  backendUrl: string;
  setBackendUrl: (value: string) => void;

  /** The API client for making API calls. */
  client: ReturnType<typeof createClient>;
  setAuthToken: (token: string) => void;
}

export interface DemoAppContextProviderProps {
  children: ReactNode | ReactNode[];
}

export const DemoAppContext = createContext<IDemoAppContext | null>(null);

export const DemoAppContextProvider = (props: DemoAppContextProviderProps) => {
  const [clientId, _setClientId] = useState(
    localStorage.getItem('clientId') || 'test-client-id-1234',
  );

  const [doorPasswordProvided, _setDoorPasswordProvided] = useState<boolean>(
    JSON.parse(localStorage.getItem('doorPasswordProvided') ?? 'false'),
  );

  const [backendUrl, setBackendUrl] = useState(
    localStorage.getItem('backendUrl') || process.env.REACT_APP__BASE_URL || '',
  );

  // Create API client with current config
  const client = useMemo(() => {
    return createClient({
      baseUrl: backendUrl,
      authToken: localStorage.getItem('authToken') || '',
      clientId: localStorage.getItem('clientId') || '',
    });
  }, [backendUrl]);

  const setAuthToken = useCallback(
    (token: string) => {
      localStorage.setItem('authToken', token);
      client.setToken(token);
    },
    [client],
  );

  const setClientid = useCallback(
    (value: string) => {
      localStorage.setItem('clientId', value);
      _setClientId(value);
    },
    [_setClientId],
  );

  const setDoorPasswordProvided = useCallback(
    (value: boolean) => {
      localStorage.setItem('doorPasswordProvided', JSON.stringify(value));
      _setDoorPasswordProvided(value);
    },
    [_setDoorPasswordProvided],
  );

  // Listeners for storage updates, which synch back to state
  const handleStorageChange = useCallback(
    (event: StorageEvent) => {
      if (event.key === 'authToken') {
        client.setToken(event.newValue ?? '');
      } else if (event.key === 'clientId') {
        _setClientId(event.newValue ?? '');
      } else if (event.key === 'doorPasswordProvided') {
        _setDoorPasswordProvided(JSON.parse(event.newValue ?? 'false'));
      }
    },
    [client, _setClientId, _setDoorPasswordProvided],
  );

  useEffect(() => {
    console.log('listening to storage event');
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleStorageChange]);

  return (
    <DemoAppContext.Provider
      value={{
        doorPasswordProvided,
        setDoorPasswordProvided,
        clientId,
        setClientid,
        backendUrl,
        setBackendUrl,
        client,
        setAuthToken,
      }}
    >
      {doorPasswordProvided && props.children}
      {!doorPasswordProvided && <LoginScreen />}
    </DemoAppContext.Provider>
  );
};

export const useDemoAppContext = (): IDemoAppContext => {
  const context = useContext(DemoAppContext);

  if (!context) {
    throw new Error('useAppContext must be used within an AppContextProvider');
  }

  return context;
};
